import { gql } from "@apollo/client";
import { faPlus, faSackXmark } from "@fortawesome/free-solid-svg-icons";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import Page, { PageActionsType } from "components/common/page/Page";
import DateView from "components/common/views/DateView";
import { uuid } from "config/app-constants";
import { graph } from "config/axios-context";
import { AppRoutes, URI_PARAMETER_KEYS } from "config/router";
import { useParams } from "react-router-dom";
import { AccommodationMaintenanceDatalistQuery, AccommodationMaintenanceDatalistQueryVariables, AccommodationWorkMaintenanceFilterInput, AccommodationWorkMaintenanceSortInput, SortEnumType } from "types/graphql";
import { Roles } from "utilities/app-utilities";
import { DatalistFilterDefinitions, appendFilters } from "utilities/datalist/datalist-filter-utilities";
import { DatalistColumnDefinitions } from "utilities/datalist/datalist-utilities";
import { DateFormats } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";

type Query = AccommodationMaintenanceDatalistQuery;
type Variables = AccommodationMaintenanceDatalistQueryVariables;
type ListType = NonNullable<Query['accommodationWorkMaintenances']>['items'][0];

const query = gql`
query AccommodationMaintenanceDatalist($where: AccommodationWorkMaintenanceFilterInput, $sort: [AccommodationWorkMaintenanceSortInput!], $skip: Int, $take: Int) {
    accommodationWorkMaintenances {
      items(where: $where, skip:$skip, take: $take, order: $sort) {
        id
        workers {
          count
          priceSum
        }
        season {
          name
        }
        startDate
        endDate
        name
        logs {
          items {
            maintenanceType {
              name
            }
            investment {
              name
            }
            id
          }
        }
      }
      count(where: $where)
    }
  }
`;

const AccommodationMaintenanceDatalistPage = () => {
    const { [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: targetSeasonId } = useParams<{ [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: string }>();
    const context = useAppContext();

    const pageActions: PageActionsType = [
        {
            icon: faSackXmark,
            name: "pages.accommodation.work.investment.list",
            title: "pages.accommodation.work.investment.list",
            link: () => {
                const url = targetSeasonId ? AppRoutes.ACCOMMODATION_SEASON_OVERVIEW_INVESTMENT : AppRoutes.ACCOMMODATION_OVERVIEW_INVESTMENT;
                const params = targetSeasonId ? { [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: targetSeasonId } : undefined;
                context.navigate(url, params);
            }
        },
        {
            icon: faPlus,
            link: AppRoutes.ACCOMMODATION_MAINTENANCE_FORM,
            name: "button.add",
            title: "button.add",
            hide: !context.userHasRole(Roles.ACCOMMODATION_WORK_WRITE) || targetSeasonId ? true : false
        }
    ];

    const datalistConfig: DatalistPropsType<ListType, AccommodationWorkMaintenanceFilterInput, AccommodationWorkMaintenanceSortInput> = {
        itemId: i => i.id,
        name: 'agriculture-maintenance-datalist-page',
        defaultView: 'table',
        onItemClick: {
            onClick: i => context.navigate(AppRoutes.ACCOMMODATION_MAINTENANCE_VIEW, { id: i.id }),
            type: "doubleClick"
        },
        dataSource: {
            request: async (params) => {
                const variables: Variables = {
                    take: params.take,
                    skip: params.skip,
                    sort: params.sort,
                    where: params.filters
                }

                if (params.searchText) {
                    const searchText: AccommodationWorkMaintenanceFilterInput = {
                        name: {
                            contains: params.searchText
                        },
                        season: {
                            name: {
                                contains: params.searchText
                            }
                        }
                    }

                    variables.where = appendFilters(variables.where, searchText);
                }

                if (targetSeasonId) {
                    variables.where = appendFilters(variables.where, {
                        seasonId: {
                            eq: Number(targetSeasonId)
                        }
                    });
                }

                const response = await graph<Query, Variables>({
                    query,
                    variables
                });

                if (response.errors) {
                    throw response.errors
                }

                return response.data!.accommodationWorkMaintenances!
            },
            sort: [
                {
                    name: 'startDate',
                    direction: SortEnumType.Desc,
                    id: uuid()
                },
                {
                    name: 'name',
                    direction: SortEnumType.Asc,
                    id: uuid()
                }
            ],
            filter: targetSeasonId ? undefined : {
                id: uuid(),
                logic: 'and',
                filters: [DatalistFilterDefinitions.seasonFilterDefinition(context)]
            }
        },
        table: {
            columns: [
                {
                    name: 'name',
                    title: 'fields.name.name',
                    resolve: i => i.name,
                    sort: true,
                    filter: {
                        type: 'string'
                    }
                },
                {
                    name: 'startDate',
                    title: 'fields.date.name',
                    resolve: i => <DateView date={i.startDate} endDate={i.endDate} format={DateFormats.SHORT} />,
                    sort: true,
                    filter: {
                        type: 'dateOnly'
                    }
                },
                {
                    name: 'endDate',
                    title: 'fields.endDate.name',
                    resolve: i => i.endDate,
                    sort: true,
                    filter: {
                        type: 'dateOnly'
                    },
                    hide: true
                },
                {
                    name: 'workers',
                    title: 'fields.workers.name',
                    resolve: i => `${i.workers.count} (${i.workers.priceSum} €)`
                },
                {
                    name: 'logs',
                    title: 'fields.type.name',
                    resolve: i => i.logs.items.map(l => l.maintenanceType.name).join(', '),
                },
                {
                    name: 'investment',
                    title: 'fields.investment.name',
                    resolve: i => {
                        const list = i.logs.items.filter(x => x.investment).map(l => l.investment?.name).join(', ');
                        return list ? list : '-';
                    },
                },
                {
                    ...DatalistColumnDefinitions.seasonColumnDefinition(context)
                }
            ],
            render: {
                actions: {
                    mutationPermissionRole: Roles.ACCOMMODATION_WORK_WRITE,
                    viewUrl: AppRoutes.ACCOMMODATION_MAINTENANCE_VIEW
                }
            }
        }
    }

    return (
        <Page wFull title="pages.accommodation.work.maintenance.list" actions={pageActions}>
            <Datalist {...datalistConfig} />
        </Page>
    );
};

export default AccommodationMaintenanceDatalistPage;
